import { useCallback, useMemo, type ReactNode } from "react"

import type { RoutedDocument, RoutedDocuments } from "../application/routed-documents"
import { ViewStateScopeProvider, ViewStateUrlProvider } from "./component-view-state"
import { normalizeUrlViewStateValues } from "./view-state-url"
import { DocumentViewStateProvider, type DocumentViewState } from "./document-view-state"

/** URL and presentation state belong to an instance; persistent route values seed new instances. */
export function RoutedDocumentViewStateProvider<TDocument>({
  document,
  setDocumentUrlValue,
  viewState,
  children,
}: {
  readonly document: RoutedDocument<TDocument>
  readonly setDocumentUrlValue: RoutedDocuments<TDocument>["setDocumentUrlValue"]
  readonly viewState: DocumentViewState
  readonly children: ReactNode
}) {
  const values = useMemo(
    () => normalizeUrlViewStateValues((document.routeLocation?.search ?? {}) as Record<string, unknown>),
    [document.routeLocation?.search]
  )
  const onValueChange = useCallback(
    (id: string, value: string | undefined) => setDocumentUrlValue(document.id, id, value),
    [document.id, setDocumentUrlValue]
  )
  const pathname = document.routeLocation?.pathname ?? document.location.split(/[?#]/, 1)[0]

  return (
    <ViewStateUrlProvider values={values} onValueChange={onValueChange}>
      <ViewStateScopeProvider id={pathname} inherit={false}>
        <DocumentViewStateProvider store={viewState} instanceId={document.id}>
          {children}
        </DocumentViewStateProvider>
      </ViewStateScopeProvider>
    </ViewStateUrlProvider>
  )
}
